import React, { useEffect, useMemo, useState } from "react";
import logo from "../../assets/images/logo.png";
import "../../assets/css/layout.css";
import "./HaklaiTabContainer.css";
import { Tooltip, Typography } from "@material-ui/core";
import { HAKLAI_TAB_LABELS } from "./haklaiTabTexts";

/**
 * Owns the Haklai tab shell.
 *
 * Responsibilities:
 * - renders the Haklai top navigation and route switch
 * - passes each tab its scoped state/functions from the parent container
 * - keeps tab chrome separate from calculator/results implementation details
 */

const HaklaiTabContainer = (props) => {
  const [visitedTabKeys, setVisitedTabKeys] = useState(() => ({
    [props.currentNavFocused]: true
  }));

  useEffect(() => {
    setVisitedTabKeys((prev) => {
      let changed = false;
      const next = { ...prev, [props.currentNavFocused]: true };
      if (!prev[props.currentNavFocused]) changed = true;

      props.optionalTabs.forEach((tab) => {
        if (tab.isDisabled && next[tab.key]) {
          delete next[tab.key];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [props.currentNavFocused, props.optionalTabs]);

  const renderedTabs = useMemo(() => {
    const activeVisitedTabKeys = {
      ...visitedTabKeys,
      [props.currentNavFocused]: true
    };
    return props.optionalTabs.filter((tab) => activeVisitedTabKeys[tab.key] && !tab.isDisabled);
  }, [props.currentNavFocused, props.optionalTabs, visitedTabKeys]);

  const optionalTabs = props.optionalTabs.map((tab) => (
    <Tooltip title={tab.isDisabled ? tab.disabledTooltip || tab.text : tab.text} key={"tab-" + tab.key}>
      <div
        onClick={() => {
          props.onTopNavChanged(tab.key);
        }}
        className={`top-nav-btns inline-block${props.currentNavFocused === tab.key ? " top-nav-btns-focused" : ""}${tab.isDisabled ? " top-nav-btns-disabled" : ""}`}
        aria-disabled={tab.isDisabled ? "true" : undefined}
      >
        <div className="icon-alignment inline-block">{tab.icon}</div>
        <div className="inline-block sm-hide-txt">
          <Typography>{tab.text}</Typography>
        </div>
        {props.currentNavFocused === tab.key ? <div className="tab-selected-connnector" /> : null}
      </div>
    </Tooltip>
  ));
  const routes = (
    <div className="inner-con">
      {renderedTabs.map((tab) => {
        const isActive = props.currentNavFocused === tab.key;
        return (
          <div
            key={"route-" + tab.key}
            hidden={!isActive}
            style={{ display: isActive ? undefined : "none" }}
          >
            {tab.component({ ...props[tab.stateFamily], ...tab.functions })}
          </div>
        );
      })}
    </div>
  );
  return (
    <>
      <div className="relative height100 haklai-tab-container">
        <div className="description inline-block">
          <Typography variant="h5">{props.title}</Typography>
          {props.subTitle.length > 40 ? (
            <Tooltip title={props.subTitle}>
              <Typography variant="h6" style={{ wordBreak: "break-word" }}>
                {props.subTitle.substring(0, 40) + "..."}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="h6" style={{ wordBreak: "break-word" }}>
              {props.subTitle || HAKLAI_TAB_LABELS.defaultDescription}
            </Typography>
          )}
        </div>

        <div className="optional-tabs-con">{optionalTabs}</div>

        <div className="small-logo-con inline-block">
          <img alt="btax-logo" src={logo} />
        </div>
      </div>

      {routes}
    </>
  );
};

export default React.memo(HaklaiTabContainer);
